import type { GetDesignerNodeById } from "../../state/index.ts";
import type { DesignerNode } from "../../types/index.ts";

// centralising logic to handle nodes that dereference other nodes
// in this case just layout.slot but if other nodes then we could signal in designer manifests

export type SlotRef = { name: string; index?: number };

// Content node ids the owning layoutable provides for the named slot refs in its layout, keyed by slot
// ref name (a single id, or ids for a multi-value slot). Set in getChildContexts and threaded down via
// parentContext to the slot (e.g. the activity frame maps its content slot to its placeholder child).
export type SlotRefContentIds = Record<string, number | number[] | undefined>;

export const getContentRef = (node: Pick<DesignerNode, "id" | "typeId" | "config">): number | SlotRef | undefined => {
  if (
    node.typeId === "layout.slot" ||
    (node.typeId === "flexbox.container" && "nodeReferences" in node.config && node.config.nodeReferences.contentId)
  ) {
    const config = node.config as
      | { nodeReferences: { contentId: number } }
      | { slotRef?: { name: string; index?: number } };
    if ("slotRef" in config && config.slotRef) {
      return config.slotRef;
    } else if ("nodeReferences" in config && config.nodeReferences.contentId) {
      return config.nodeReferences.contentId;
    } else {
      throw new Error(`No content ref in layout node ${node.typeId}:${node.id}`);
    }
  }
};

export const resolveSlotRefContentId = (
  slotRef: SlotRef,
  slotRefContentIds: SlotRefContentIds | undefined,
): number | undefined => {
  const provided = slotRefContentIds?.[slotRef.name];
  return Array.isArray(provided) ? provided[slotRef.index ?? 0] : provided;
};

interface Options {
  getById: GetDesignerNodeById;
  // The slot ref name -> content node id mapping for this node, from its parentContext. A slotRef that
  // resolves to nothing throws (fail fast) rather than silently rendering unlinked.
  slotRefContentIds?: SlotRefContentIds;
}

export const getContentNode = (
  node: Pick<DesignerNode, "id" | "typeId" | "config">,
  { getById, slotRefContentIds }: Options,
) => {
  const nodeIdOrSlotRef = getContentRef(node);
  if (typeof nodeIdOrSlotRef === "number") {
    return getById(nodeIdOrSlotRef);
  }
  if (nodeIdOrSlotRef) {
    const contentId = resolveSlotRefContentId(nodeIdOrSlotRef, slotRefContentIds);
    if (contentId == null) {
      throw new Error(`Slot ref "${nodeIdOrSlotRef.name}" was not provided a content node by its layoutable.`);
    }
    return getById(contentId);
  }
  return undefined;
};
